import { useMemo } from 'react';
import { useRevalidator } from 'react-router-dom';

import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import usePendingGroupInvites from '~/routes/subscribe/$subscriberId/_components/v2/hooks/usePendingGroupInvites';
import { useResendSubscriptionChangeEmail } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useResendSubscriptionChangeEmail';
import { useResetPassword } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useResetPassword';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';
import logout from '~/utils/logout';

import { AccountProfileContext, type AccountProfileContextType } from './AccountProfileContext';
import { useIdentitiesContext } from './useIdentitiesContext';
import { useModalCoordinationContext } from './useModalCoordinationContext';

type Props = {
  subscription: Subscription;
  site: Site;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the account profile / password surface on the General tab — the profile card data, the
 * edit-profile and set/reset-password modals, and their openers (incl. logout + the
 * connected-accounts opener). Identity/integration fetching and the OAuth redirect handling live
 * in the tab-agnostic `IdentitiesProvider`; this reads `integrations` from it for the card's
 * social-login links. Renders no UI; the colocated `AccountProfileSection` and
 * `AccountProfileModals` children read from context.
 */
export const AccountProfileProvider = ({ subscription, site, subscriberId, isLoggedIn, children }: Props) => {
  const publication = site.publication;
  const isSubscriptionInvalid = isInvalidSubscription(subscription);

  const { state: revalidateState } = useRevalidator();
  const isRevalidating = revalidateState === 'loading';

  const {
    global: { handleOpenModal: handleOpenGlobalModal },
    local: { handleOpenModal, handleCloseModal, currentModal },
  } = useModalCoordinationContext();

  const { integrations } = useIdentitiesContext();

  const { hasSetPassword, canResetPassword } = useResetPassword(site, subscription, isSubscriptionInvalid);
  const { onResend, isLoading: isResendingConfirmationEmail } = useResendSubscriptionChangeEmail(
    subscriberId,
    publication.id,
  );

  // Client-fetched so pending invites render on the General tab regardless of
  // which route rendered Page (only manage.tsx's loader fetched them before).
  const { invites: pendingGroupInvites } = usePendingGroupInvites({ enabled: isLoggedIn });

  const {
    email,
    first_name: firstName,
    last_name: lastName,
    avatar_url: avatarUrl,
    has_email_change_pending: hasEmailChangePending,
    created_at: createdAt,
    premium_since: premiumSince,
  } = subscription || {};

  const value = useMemo<AccountProfileContextType>(
    () => ({
      email,
      firstName,
      lastName,
      avatarUrl,
      hasEmailChangePending,
      createdAt,
      premiumSince,
      showProfileActions: isLoggedIn && !isSubscriptionInvalid,
      canResetPassword,
      socialLoginProviders: integrations,
      isProfileLoading: isRevalidating,
      pendingGroupInvites,

      onClickEditProfile: () => handleOpenModal('edit_profile'),
      onClickConnections: () => handleOpenGlobalModal('connected_accounts'),
      onClickResetPassword: () => handleOpenModal('reset_password'),
      onClickLogout: () => logout('token'),

      isLoggedIn,
      subscriberId,
      currentModal,
      closeAccountModal: () => handleCloseModal(),
      hasSetPassword,
      onResend,
      isResendingConfirmationEmail,
    }),
    [
      email,
      firstName,
      lastName,
      avatarUrl,
      hasEmailChangePending,
      createdAt,
      premiumSince,
      isLoggedIn,
      isSubscriptionInvalid,
      canResetPassword,
      integrations,
      isRevalidating,
      pendingGroupInvites,
      handleOpenModal,
      handleOpenGlobalModal,
      handleCloseModal,
      subscriberId,
      currentModal,
      hasSetPassword,
      onResend,
      isResendingConfirmationEmail,
    ],
  );

  return <AccountProfileContext.Provider value={value}>{children}</AccountProfileContext.Provider>;
};
